import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  badge: string;
  title: string;
  subtitle: string;
  tags: string[];
  image: string;
}

interface HeroCarouselProps {
  slides: Slide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  const active = slides[index];

  return (
    <section
      className="relative h-[480px] w-full overflow-hidden md:h-[620px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="热门影视推荐"
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点推荐剧照`}
            title={`${slide.title} 热门推荐`}
            className="h-full w-full object-cover"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/60 to-background-950/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 via-transparent to-transparent" />

      <div className="relative flex h-full w-full items-end">
        <div className="w-full px-4 pb-16 md:px-10 md:pb-24">
          <div key={index} className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-700 text-background-950">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-fire-fill text-[11px]" aria-hidden="true" />
              </span>
              {active.badge}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-800 leading-tight text-foreground-50 md:text-5xl">
              {active.title}
            </h2>
            <p className="mt-3 line-clamp-2 max-w-xl text-sm text-foreground-200 md:text-base">
              {active.subtitle}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {active.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-secondary-500/50 bg-secondary-500/10 px-3 py-0.5 text-xs text-secondary-300"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-play-fill" aria-hidden="true" />
                </span>
                查看详情
              </Link>
              <Link
                to="/category/all"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-50/30 bg-background-950/40 px-6 py-2.5 text-sm font-600 text-foreground-50 backdrop-blur-sm transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-grid-line" aria-hidden="true" />
                </span>
                浏览全部
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 flex items-center gap-3 md:right-10">
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + slides.length) % slides.length)}
            aria-label="上一个推荐"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-foreground-50/25 bg-background-950/50 text-foreground-100 backdrop-blur-sm transition-colors hover:border-accent-500 hover:text-accent-400"
          >
            <i className="ri-arrow-left-s-line" aria-hidden="true" />
          </button>
          <div className="flex items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`切换到 ${slide.title}`}
                className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
                  i === index ? "w-6 bg-accent-500" : "w-1.5 bg-foreground-50/40"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % slides.length)}
            aria-label="下一个推荐"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-foreground-50/25 bg-background-950/50 text-foreground-100 backdrop-blur-sm transition-colors hover:border-accent-500 hover:text-accent-400"
          >
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}